import log from 'loglevel';
import type { UserInfo, Authtoken, LocationInfo } from '../../types';
import { GreetingTaskDefinition, GreetingTaskEventType } from './greetings';

export enum TaskType {
    POPUP = 'popup',
}
export type TaskId = string;
export type BaseTaskDefinition = {
    type: TaskType;
    id: TaskId;
};
export type TaskDefinition = BaseTaskDefinition & GreetingTaskDefinition;

export type TaskEventType = GreetingTaskEventType;
export { GreetingTaskEventType };

export type TaskEventData = Record<string, any>;

export type FetchActiveTasksResponse = Array<TaskDefinition>;

export type FetchActiveTasks = (
    locationInfo?: LocationInfo,
    userInfo?: UserInfo,
    authtoken?: Authtoken
) => Promise<FetchActiveTasksResponse>;
export type PostTaskEvent = (
    type: TaskType,
    id: TaskId,
    event: TaskEventType,
    locationInfo?: LocationInfo,
    userInfo?: UserInfo,
    data?: TaskEventData
) => Promise<void>;
export type Abort = () => void;

export interface TasksBackendClient {
    fetchActiveTasks: FetchActiveTasks;
    postTaskEvent: PostTaskEvent;
    abort: Abort;
}

export const newTasksBackendClient = (
    baseUrl: string,
    fetchImpl: Function = fetch
): TasksBackendClient => {
    const abortController = new AbortController();
    const getTimestamp = () => new Date().toISOString();

    return {
        fetchActiveTasks: async (
            locationInfo?: LocationInfo,
            userInfo?: UserInfo,
            authtoken?: Authtoken
        ): Promise<FetchActiveTasksResponse> => {
            const url = `${baseUrl}/widget/tasks/active`;

            return fetchImpl(url, {
                signal: abortController.signal,
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    host: locationInfo?.host,
                    location: locationInfo?.pathname,
                    timestamp: getTimestamp(),
                    customerId: userInfo?.customerId,
                    userId: userInfo?.userId,
                    authtoken: authtoken,
                }),
            })
                .then(async (response: Response) => {
                    const status = response.status;
                    if (status < 200 || status > 299) {
                        throw {
                            message: 'backend responded with error on fetching active tasks',
                            url,
                            status,
                            locationInfo,
                            userInfo,
                        };
                    }

                    const data = (await response.json()) as FetchActiveTasksResponse;
                    log.debug('success while fetching active tasks', {
                        status,
                        data,
                        locationInfo,
                        userInfo,
                    });
                    return data;
                })
                .catch((error: Error) => {
                    if (error?.name === 'AbortError') {
                        log.debug('http client request aborted while fetching active tasks', {
                            url,
                            locationInfo,
                            userInfo,
                            error,
                        });
                        return [];
                    } else {
                        log.warn('http client error while fetching active tasks', {
                            url,
                            locationInfo,
                            userInfo,
                            error,
                        });
                        throw error;
                    }
                });
        },

        postTaskEvent: async (
            type: TaskType,
            id: TaskId,
            event: TaskEventType,
            locationInfo?: LocationInfo,
            userInfo?: UserInfo,
            data?: TaskEventData
        ): Promise<void> => {
            const url = `${baseUrl}/widget/tasks/event`;

            return fetchImpl(url, {
                signal: abortController.signal,
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    ...data,
                    type,
                    id,
                    event,
                    host: locationInfo?.host,
                    location: locationInfo?.pathname,
                    customerId: userInfo?.customerId,
                    userId: userInfo?.userId,
                    timestamp: getTimestamp(),
                }),
            })
                .then(({ status }: Response) => {
                    if (status < 200 || status > 299) {
                        throw {
                            message: 'backend responded with error on posting task event',
                            type,
                            id,
                            event,
                            url,
                            status,
                            locationInfo,
                            userInfo,
                        };
                    }

                    log.debug('success while posting task event', {
                        type,
                        id,
                        event,
                        url,
                        status,
                        locationInfo,
                        userInfo,
                    });
                })
                .catch((error: Error) => {
                    if (error?.name === 'AbortError') {
                        log.debug('http client request aborted while posting task event', {
                            type,
                            id,
                            event,
                            url,
                            locationInfo,
                            userInfo,
                            error,
                        });
                    } else {
                        log.warn('http client error while posting task event', {
                            type,
                            id,
                            event,
                            url,
                            locationInfo,
                            userInfo,
                            error,
                        });
                        throw error;
                    }
                });
        },

        abort: () => {
            abortController.abort();
        },
    };
};
